// src/modules/app-views/account/service.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import dayjs from 'dayjs';
import { clone, isMongoObjectId, throwError } from 'utils/helpers';
import axiosService from 'services/axiosService';
import authService from 'services/authService';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import app from 'configs/ConfigIndex';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CONTEXT = 'accountService';
// const INVITATION_EXPIRES_MINUTES = 2880;

export const SUBSCRIPTION_PROFILE_OPTIONS = [
  { value: 'retail', label: 'profile_retail' },
  { value: 'clinic', label: 'profile_clinic' },
  { value: 'furniture', label: 'profile_furniture' },
  { value: 'technical_assistance', label: 'profile_technical_assistance' },
  { value: 'services', label: 'profile_services' },
];

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************

// *******************************************************************************
// MODULE METHODS
// *******************************************************************************
const decorator = doc => {
  const document = clone(doc);

  if (document?.birthdate) document.birthdate = dayjs(document.birthdate);

  // return document || storedPageSettings?.doc || null;
  return document || null;
};

// *******************************************************************************
// TEAM METHODS
// *******************************************************************************
// Resolve effective subscriptionId: explicit arg wins over active (zustand) one.
// Team operations on the Subscription document page MUST pass the document._id
// so operations hit the correct subscription (not the active one).
function resolveSubscriptionId(subscriptionId) {
  if (subscriptionId) return subscriptionId;
  const active = useZustand('auth', 'subscription', false);
  return active?._id || null;
}

// Build headers that override x-subscriptionid (default set globally from auth.subscription)
function buildSubscriptionHeaders(subscriptionId) {
  return subscriptionId ? { 'x-subscriptionid': subscriptionId } : undefined;
}

const createUser = async (data, subscriptionId) => {
  const message = await appService.message({
    flag: 'loading',
    text: 'sending_invitation',
    delay: 0,
  });

  const subId = resolveSubscriptionId(subscriptionId);

  try {
    const axiosOptions = {
      url: API.INVITATIONS.POST_INVITATION,
      data,
      headers: buildSubscriptionHeaders(subId),
    };
    await axiosService.post(axiosOptions);
    message.update({ flag: 's', text: 'invitation_sent_successfully' });
  } catch (err) {
    message.update({ flag: 'error', text: 'error_sending_invite', delay: 5 });
    throw new Error(err.message || 'Failed to send invitation');
  }
};

const resendInvitationEmail = async (data, subscriptionId) => {
  const message = await appService.message({
    flag: 'loading',
    text: 'resending_invitation',
    delay: 0,
  });

  const subId = resolveSubscriptionId(subscriptionId);

  try {
    const axiosOptions = {
      url: API.INVITATIONS.RESEND_INVITATION,
      data,
      headers: buildSubscriptionHeaders(subId),
    };
    await axiosService.post(axiosOptions);
    message.update({ flag: 's', text: 'invitation_resent_successfully' });

    return true;
  } catch (err) {
    message.update({ flag: 'error', text: 'error_resending_invite', delay: 5 });
    throw new Error(err?.message || 'Failed to resend invitation!');
  }
};

const removeUser = async (accountId, subscriptionId) => {
  const subId = resolveSubscriptionId(subscriptionId);

  const message = await appService.message({
    flag: 'loading',
    text: 'removing_user',
    delay: 0,
  });

  try {
    const axiosOptions = {
      url: mergeUrlParamsQuery({
        url: API.INVITATIONS.REMOVE_INVITATION,
        params: {
          _id: accountId,
          subscriptionId: subId,
        },
      }),
      headers: buildSubscriptionHeaders(subId),
    };
    await axiosService.delete(axiosOptions);
    message.update({ flag: 's', text: 'user_removed_successfully' });
  } catch (err) {
    message.update({ flag: 'error', text: 'error_removing_user', delay: 5 });
    throw new Error(err?.message || 'Failed to remove user!');
  }
};

const changeUserProfile = async ({ userId, profile, subscriptionId }) => {
  const subId = resolveSubscriptionId(subscriptionId);

  try {
    if (!subId) {
      throwError('Subscription is required and must have an _id');
    }

    if (!userId) {
      throwError('userId (representing accountId or invitationId) is required');
    }

    // Backend route: PUT /subscriptions/:subscriptionId/team/:accountId
    // Body: { profile, granted }
    const axiosOptions = {
      url: mergeUrlParamsQuery({
        url: API.SUBSCRIPTIONS.UPDATE_TEAM_MEMBER,
        params: {
          subscriptionId: subId,
          accountId: userId,
        },
      }),
      data: { profile },
      headers: buildSubscriptionHeaders(subId),
    };

    await axiosService.put(axiosOptions);
  } catch (err) {
    throw new Error(err.message || 'Failed to change user profile!');
  }
};

const updateInvitation = async ({ _id, name, profile, subscriptionId }) => {
  const message = await appService.message({
    flag: 'loading',
    text: 'updating_invitation',
    delay: 0,
  });

  const subId = resolveSubscriptionId(subscriptionId);

  try {
    const axiosOptions = {
      url: mergeUrlParamsQuery({
        url: API.INVITATIONS.URL,
        params: { _id },
      }),
      data: { name, profile },
      headers: buildSubscriptionHeaders(subId),
    };
    await axiosService.put(axiosOptions);
    message.update({ flag: 's', text: 'invitation_updated_successfully' });
  } catch (err) {
    message.update({ flag: 'error', text: 'error_updating_invitation', delay: 5 });
    throw new Error(err.message || 'Failed to update invitation');
  }
};

const putRemoveInviteFromInvitations = async ({ subscriptionId, invitationId }) => {
  if (!isMongoObjectId(subscriptionId)) {
    return console.error("Invalid 'subscriptionId' on putRemoveInviteFromInvitations()");
  } else if (!isMongoObjectId(invitationId)) {
    return console.error("Invalid 'invitationId' on putRemoveInviteFromInvitations()");
  }

  const axiosOptions = {
    url: `/v2/invitations/${invitationId}/cancel-invitation`,
    data: { subscriptionId },
    headers: { 'x-resultasobject': true },
  };

  try {
    const doc = await axiosService.put(axiosOptions);
    await authService.signInRefresh();
    return doc ?? null;
  } catch (err) {
    await appService.message('e', 'save_failed', 'onSave');
    throw err;
  }
};

const putRemoveInviteFromSubscriptions = async ({
  subscriptionId,
  accountId,
  updatedByAccountId,
}) => {
  if (!isMongoObjectId(subscriptionId)) {
    return console.error("Invalid 'subscriptionId' on putRemoveInviteFromSubscriptions()");
  } else if (!isMongoObjectId(accountId)) {
    return console.error("Invalid 'accountId' on putRemoveInviteFromSubscriptions()");
  }

  const axiosOptions = {
    url: `/v1/subscription/${subscriptionId}/cancel-invitation`,
    data: {
      accountId,
      _metadata: {
        audit: {
          updatedAt: new Date(),
          updatedByAccountId,
        },
      },
    },
    headers: { 'x-resultasobject': true },
  };

  try {
    const doc = await axiosService.put(axiosOptions);
    await authService.signInRefresh();
    return doc ?? null;
  } catch (err) {
    await appService.message('e', 'save_failed', 'onSave');
    throw err;
  }
};

const changeTeamStatus = async ({ subscriptionId, accountId, updatedByAccountId, status }) => {
  if (!isMongoObjectId(subscriptionId)) {
    return console.error("Invalid 'subscriptionId' on changeTeamStatus()");
  } else if (!isMongoObjectId(accountId)) {
    return console.error("Invalid 'accountId' on changeTeamStatus()");
  }

  const axiosOptions = {
    url: `/v1/subscription/${subscriptionId}/change-team-status`,
    data: {
      accountId,
      status,
      _metadata: {
        audit: {
          updatedAt: new Date(),
          updatedByAccountId,
        },
      },
    },
    headers: { 'x-resultasobject': true },
  };

  try {
    const doc = await axiosService.put(axiosOptions);
    await authService.signInRefresh();
    return doc ?? null;
  } catch (err) {
    await appService.message('e', 'save_failed', 'onSave');
    throw err;
  }
};

const depositSparkCoins = async ({ accountId, amount, description, userName }) => {
  try {
    await axiosService.put({
      url: '/v1/subscriptions/team/sparks/deposit',
      data: { accountId, amount, description, userName },
    });
    return true;
  } catch (err) {
    return false;
  }
};

const hasNextStep = stepKey => {
  const storedUser = useZustand('auth', 'user', false);
  const wns = storedUser?.welcomeNextSteps || [];
  return wns.includes(stepKey);
};

/**
 * Navigate to account settings page
 * Encapsulates the navigation logic for reuse across components
 * @param {Object} options - Navigation options
 * @param {Function} options.navigate - React Router navigate function
 * @param {string} [options.tab='my_profile'] - Tab to open (my_profile, subscriptions, etc.)
 * @param {boolean} [options.replace=true] - Replace current history entry
 */
const navigateToAccountSettings = ({ navigate, tab = 'my_profile', replace = true }) => {
  const storedUser = useZustand('auth', 'user', false);
  const setLoadingModule = useZustand('boot', 'setLoadingModule', false);
  const setCurrentRouteKey = useZustand('module', 'setCurrentRouteKey', false);
  const setSelectedMenuKeyNavProfile = useZustand('app', 'setSelectedMenuKeyNavProfile', false);

  if (!storedUser?._id || !navigate) return;

  const path = 'app/account';

  // Show loading state
  setLoadingModule?.(true);

  // Update route keys for menu highlighting
  setCurrentRouteKey?.(path);
  setSelectedMenuKeyNavProfile?.(path);

  // Navigate with small delay for loading animation
  setTimeout(() => {
    navigate(
      {
        pathname: `/${path}/${storedUser._id}`,
        search: `?tab=${tab}`,
      },
      { replace }
    );
  }, 300);
};

const putNextStep = async stepKey => {
  const storedUser = useZustand('auth', 'user', false);

  // Array atual de steps completados pelo usuário
  const currentSteps = storedUser?.welcomeNextSteps || [];

  // Array de todos os steps definidos no app
  const allSteps = app.WELCOME_NEXT_STEPS || [];

  // Simula o estado após adicionar o stepKey atual
  const futureSteps = [...currentSteps, stepKey];

  // Verifica se todos os steps estarão completos após este PUT
  const allCompleted = allSteps.every(step => futureSteps.includes(step));

  if (
    allCompleted &&
    !appService.getModulePreferences({
      moduleId: 'welcome',
      category: 'tour',
      key: 'done',
      ignoreSubscription: true,
    })
  ) {
    appService
      .updateModulePreferences({
        category: 'tour',
        data: { done: true },
        moduleId: 'welcome',
        ignoreSubscription: true,
      })
      .then();
  }

  const value = [stepKey];
  const params = { _id: storedUser._id, operator: '$push', path: 'welcomeNextSteps' };
  const axiosConfig = {
    url: mergeUrlParamsQuery({ url: API.ACCOUNTS.ADD_TO_SET, params }),
    data: { value },
  };

  await axiosService.put(axiosConfig);
  await authService.signInRefresh();
};

const removeNextStep = async stepKey => {
  const storedUser = useZustand('auth', 'user', false);

  const value = [stepKey];
  const params = { _id: storedUser._id, operator: '$pull', path: 'welcomeNextSteps' };
  const axiosConfig = {
    url: mergeUrlParamsQuery({ url: API.ACCOUNTS.ADD_TO_SET, params }),
    data: { value },
  };

  await axiosService.put(axiosConfig);
  await authService.signInRefresh();
};

// *******************************************************************************
// SERVICES
// *******************************************************************************
const accountService = {
  createUser,
  resendInvitationEmail,
  removeUser,

  decorator,
  changeTeamStatus,

  changeUserProfile,
  updateInvitation,
  putRemoveInviteFromSubscriptions,
  putRemoveInviteFromInvitations,
  depositSparkCoins,
  putNextStep,
  hasNextStep,
  removeNextStep,
  navigateToAccountSettings,
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default accountService;
